import React, { useEffect, useState } from 'react';
import BrandLogo from '../components/BrandLogo';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, Clock, CheckCircle2, AlertCircle, Download, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client/login');
      return;
    }
    setClientName(localStorage.getItem('client_name') || '');
    fetchJobs();
  }, [navigate]);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('client_token');
      const { data } = await axios.get(`${API}/client/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_name');
    localStorage.removeItem('client_email');
    navigate('/client/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Revision Requested': 'bg-orange-100 text-orange-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-800';
  };

  const getClientStatusColor = (status) => {
    const colors = {
      'Approved': 'bg-green-100 text-green-800',
      'Needs Revision': 'bg-orange-100 text-orange-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-600">Loading your jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BrandLogo className="h-8" />
              <div className="hidden sm:block">
                <span className="text-sm text-zinc-500">Client Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-600 hidden sm:block">
                Welcome, <strong>{clientName}</strong>
              </span>
              <Button
                data-testid="logout-btn"
                variant="outline"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Jobs
          </h1>
          <p className="text-zinc-600">View and manage your test jobs</p>
        </div>

        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
            <FileText className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No jobs found</h3>
            <p className="text-zinc-600 mb-6">You haven&apos;t submitted any test jobs yet.</p>
            <Button onClick={() => navigate('/')}>
              Submit a Test Job
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                data-testid={`job-card-${job.id}`}
                className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/client/jobs/${job.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-zinc-900">Job #{job.id.slice(0, 8)}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                      {job.client_status && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getClientStatusColor(job.client_status)}`}>
                          Your Status: {job.client_status}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-zinc-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(job.created_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {job.files?.length || 0} files uploaded
                      </div>
                      {job.retouched_files?.length > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <Download className="w-4 h-4" />
                          {job.retouched_files.length} retouched files ready
                        </div>
                      )}
                    </div>

                    {job.notes && (
                      <p className="mt-3 text-sm text-zinc-500 line-clamp-2">
                        Notes: {job.notes}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-sm text-zinc-500">
        <p>Need help? Contact us at <a href="mailto:contact@retouchingpix.com" className="text-blue-600 hover:underline">contact@retouchingpix.com</a></p>
      </footer>
    </div>
  );
};

export default ClientDashboard;
